import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from '../components/Header';
import Footer from '../components/Footer';

const { FiArrowRight, FiClock, FiUser, FiTrendingUp, FiDollarSign, FiShield, FiUsers, FiBarChart3, FiAlertCircle, FiCheckCircle, FiMail } = FiIcons;

const ValuationPage = () => {
  React.useEffect(() => {
    // SEO Meta Tags
    document.title = "HVAC Valuations in 2025: What Drives Multiples & Sale Prices | HVACBizBroker";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Understand real HVAC multiples, EBITDA vs. SDE, and what buyers look for when valuing HVAC companies in 2025.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Understand real HVAC multiples, EBITDA vs. SDE, and what buyers look for when valuing HVAC companies in 2025.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'HVAC valuation 2025, EBITDA multiples HVAC, selling HVAC company, private equity HVAC');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'HVAC valuation 2025, EBITDA multiples HVAC, selling HVAC company, private equity HVAC';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  const scrollToValuation = () => {
    window.location.href = '/#valuation';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Blog Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-blue-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              HVAC Valuations in 2025: Real Multiples, SDE vs. EBITDA & What Drives Price
            </h1>
            <div className="flex items-center justify-center space-x-6 text-gray-300 mb-8">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUser} className="w-4 h-4" />
                <span>HVAC Biz Broker Team</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>15 min read</span>
              </div>
            </div>
            
            {/* Top CTA */}
            <div className="bg-blue-800/50 backdrop-blur-sm p-6 rounded-lg mb-8">
              <p className="text-lg mb-4">
                Get a confidential valuation at HVACBizBroker.com or email info@hvacbizbroker.com.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:info@hvacbizbroker.com?subject=Valuation%20Request&body=Hello%20HVAC%20Biz%20Broker%20Team,%0A%0AI%20would%20like%20to%20request%20a%20confidential%20valuation%20for%20my%20HVAC%20business.%0A%0APlease%20contact%20me%20to%20discuss%20the%20next%20steps.%0A%0AThank%20you."
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center space-x-2"
                >
                  <span>Valuation Intake Form</span>
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </a>
                <a
                  href="mailto:info@hvacbizbroker.com?subject=Confidential%20Consultation&body=Hello%20HVAC%20Biz%20Broker%20Team,%0A%0AI%20would%20like%20to%20schedule%20a%20confidential%20consultation%20regarding%20my%20HVAC%20business.%0A%0APlease%20let%20me%20know%20what%20information%20you%20need%20from%20me.%0A%0AThank%20you."
                  className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center space-x-2"
                >
                  <span>Confidential Consultation</span>
                  <SafeIcon icon={FiShield} className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          {/* Introduction */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-12 rounded-r-lg">
            <p className="text-lg text-gray-700 leading-relaxed">
              If you're an HVAC business owner considering selling or recapitalizing in 2025, you're probably wondering: 
              <strong>What's my company actually worth?</strong> The answer isn't simple, but understanding real valuation 
              multiples, the difference between SDE and EBITDA, and what buyers value can help you maximize your exit value.
            </p>
            <p className="text-lg text-gray-700 mt-4 leading-relaxed">
              At HVACBizBroker.com, we specialize in sell-side advisory for HVAC companies. Here's the straight talk on 
              valuations in today's market — no fluff, just what you need to know to prepare for a successful exit.
            </p>
          </div>

          {/* Key Quote */}
          <div className="bg-gray-100 p-6 rounded-lg mb-12 text-center">
            <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-800 italic">
              "If you don't control your valuation story, the buyer will write it for you."
            </p>
            <p className="text-gray-600 mt-2">— HVACBizBroker Advisory Team</p>
          </div>

          {/* Section 1: The Current HVAC Valuation Landscape */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 mr-3 text-blue-600" />
              The Current HVAC Valuation Landscape
            </h2>
            
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The HVAC market is experiencing unprecedented interest from private equity groups and strategic buyers. 
              Here's what we're seeing in real transactions:
            </p>

            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Real-World Multiples (2024-2025)</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-lg font-semibold text-green-800 mb-2">EBITDA Multiples</h4>
                  <p className="text-3xl font-bold text-green-600 mb-2">5.0x - 8.5x</p>
                  <p className="text-gray-700 text-sm">
                    Quality HVAC companies with strong recurring revenue
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-800 mb-2">SDE Multiples</h4>
                  <p className="text-3xl font-bold text-blue-600 mb-2">2.5x - 4.5x</p>
                  <p className="text-gray-700 text-sm">
                    Smaller businesses under $1M in earnings
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              But here's the reality: <strong>these ranges are meaningless without context</strong>. Your specific 
              multiple depends on quality metrics, growth trajectory, and buyer profile. A 6x EBITDA multiple on 
              poorly documented earnings might net you less than a 4x multiple on clean, verifiable financials.
            </p>
          </section>

          {/* Section 2: SDE vs. EBITDA - The Critical Difference */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiBarChart3} className="w-8 h-8 mr-3 text-blue-600" />
              SDE vs. EBITDA - The Critical Difference
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Understanding the difference between Seller's Discretionary Earnings (SDE) and EBITDA is fundamental 
              to positioning your business for the right valuation:
            </p>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-600">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Seller's Discretionary Earnings (SDE)</h3>
                <p className="text-gray-700 mb-3">
                  SDE = Pre-tax profit + Owner's salary + Owner's benefits + Non-recurring expenses + Personal expenses run through business
                </p>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600">
                    <strong>Used for:</strong> Companies under $2M EBITDA, typically family-owned businesses
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Multiple range:</strong> 2.5x - 4.5x (lower multiples, but base number is larger)
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">EBITDA (Earnings Before Interest, Taxes, Depreciation, Amortization)</h3>
                <p className="text-gray-700 mb-3">
                  EBITDA = Operating profit before non-cash items and financing structure
                </p>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600">
                    <strong>Used for:</strong> Companies over $2M EBITDA, institutional buyers, private equity
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Multiple range:</strong> 5.0x - 8.5x (higher multiples, more conservative earnings base)
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 mt-6">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Pro Tip:</h3>
              <p className="text-gray-700">
                Many owners make the mistake of presenting SDE to institutional buyers. If you're targeting private 
                equity, you need clean EBITDA with proper add-backs documented. This isn't about hiding income — 
                it's about speaking the buyer's language.
              </p>
            </div>
          </section>

          {/* Section 3: What Actually Drives HVAC Valuations */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiDollarSign} className="w-8 h-8 mr-3 text-blue-600" />
              What Actually Drives HVAC Valuations
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Beyond the basic financial metrics, buyers are looking for specific value drivers that justify premium multiples:
            </p>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">Revenue Growth Rate</h3>
                  </div>
                  <p className="text-gray-700">
                    15%+ YoY growth commands 1-2 multiple point premiums
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">Recurring Revenue</h3>
                  </div>
                  <p className="text-gray-700">
                    40%+ service contract revenue increases multiples by 1.5-2x
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiShield} className="w-6 h-6 text-purple-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">Customer Concentration</h3>
                  </div>
                  <p className="text-gray-700">
                    No single customer {'>'}20% of revenue (or prepare for discounts)
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-3">
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-orange-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">Management Depth</h3>
                  </div>
                  <p className="text-gray-700">
                    Non-owner-dependent operations earn 0.5-1 multiple point premium
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-100 p-6 rounded-lg mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">The Geography Premium</h3>
              <p className="text-gray-700">
                Markets like Texas, Florida, Arizona, and the Southeast command 0.5-1.5 multiple point premiums 
                due to growth potential, new construction, and favorable business climates. Northeast and Midwest 
                businesses need stronger fundamentals to command similar multiples.
              </p>
            </div>
          </section>

          {/* Section 4: Private Equity vs. Strategic Buyers */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiUsers} className="w-8 h-8 mr-3 text-blue-600" />
              Private Equity vs. Strategic Buyers - What's the Difference?
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Understanding who's buying helps position your business effectively:
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Private Equity Groups</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">Focus on EBITDA quality and growth potential</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">Value scalable processes and management teams</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">Often require seller rollover equity (20-30%)</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">Typically pay higher multiples for platform companies</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-600">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Strategic Buyers</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">Focus on market share and geographic expansion</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">Value customer relationships and brand reputation</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">May offer integration synergies (higher price)</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mt-0.5" />
                    <p className="text-gray-700">Often prefer complete exits (no rollover)</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Common Valuation Mistakes */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiAlertCircle} className="w-8 h-8 mr-3 text-red-600" />
              Common Valuation Mistakes That Cost You Money
            </h2>

            <div className="space-y-6">
              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Not Documenting Add-Backs Properly</h3>
                <p className="text-gray-700">
                  Saying "I pay myself $300k but market rate is $150k" isn't enough. Need documentation, 
                  industry benchmarks, and reasonable justification. Every undocumented add-back gets challenged.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Presenting Messy Financials</h3>
                <p className="text-gray-700">
                  QuickBooks downloads with personal expenses mixed in scream "unprofessional." 
                  Clean, reviewed financials (even if compiled) show you're serious.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Waiting Until You're Ready to Sell</h3>
                <p className="text-gray-700">
                  The best time to start preparing for sale is 2-3 years before you actually sell. 
                  Tax returns, customer contracts, employee records — these take time to organize.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Not Understanding Your Customer Concentration</h3>
                <p className="text-gray-700">
                  One client at 35% of revenue can kill a deal or cost you 1-2 multiple points. 
                  Diversify early, or at least have transition plans for major accounts.
                </p>
              </div>
            </div>
          </section>

          {/* Second Key Quote */}
          <div className="bg-gray-100 p-6 rounded-lg mb-12 text-center">
            <SafeIcon icon={FiShield} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-800 italic">
              "The best valuation is the one you control. Start preparing 24 months before you need to sell."
            </p>
            <p className="text-gray-600 mt-2">— HVACBizBroker Exit Strategy Team</p>
          </div>

          {/* Section 6: How to Maximize Your Valuation */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 mr-3 text-green-600" />
              How to Maximize Your Valuation
            </h2>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">12-Month Action Plan</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Get Professional Financials</h4>
                      <p className="text-gray-700">Move from QuickBooks to reviewed financial statements</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Document Everything</h4>
                      <p className="text-gray-700">Customer contracts, employee agreements, vendor relationships</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Build Management Depth</h4>
                      <p className="text-gray-700">Identify and develop your next layer of leadership</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Focus on Recurring Revenue</h4>
                      <p className="text-gray-700">Push service contracts to 40%+ of total revenue</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Get Professional Valuation</h4>
                      <p className="text-gray-700">Know your value before buyers start negotiations</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-lg">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Understand Your True Value?</h3>
                <p className="text-xl mb-6 opacity-90 leading-relaxed">
                  Don't leave millions on the table by guessing your valuation. Get a professional, 
                  confidential valuation from HVACBizBroker.com and position your business for the maximum multiple.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                  <a
                    href="mailto:info@hvacbizbroker.com?subject=Valuation%20Request&body=Hello%20HVAC%20Biz%20Broker%20Team,%0A%0AI%20would%20like%20to%20request%20a%20confidential%20valuation%20for%20my%20HVAC%20business.%0A%0APlease%20contact%20me%20to%20discuss%20the%20next%20steps.%0A%0AThank%20you."
                    className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center space-x-2 shadow-lg"
                  >
                    <span>Valuation Intake Form</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </a>
                  <a
                    href="mailto:info@hvacbizbroker.com?subject=Confidential%20Consultation&body=Hello%20HVAC%20Biz%20Broker%20Team,%0A%0AI%20would%20like%20to%20schedule%20a%20confidential%20consultation%20regarding%20my%20HVAC%20business.%0A%0APlease%20let%20me%20know%20what%20information%20you%20need%20from%20me.%0A%0AThank%20you."
                    className="bg-blue-800 hover:bg-blue-900 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center space-x-2"
                  >
                    <span>Email info@hvacbizbroker.com</span>
                    <SafeIcon icon={FiMail} className="w-5 h-5" />
                  </a>
                </div>
                
                <p className="text-sm opacity-75">
                  Confidential • No Obligation • Industry-Specific Valuation Methods
                </p>
              </div>
            </div>
          </section>

          {/* Internal Links */}
          <section className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Related Resources</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog" className="text-blue-600 hover:text-blue-800 transition-colors">
                ← Back to HVAC Blog
              </Link>
              <Link to="/" className="text-blue-600 hover:text-blue-800 transition-colors">
                HVACBizBroker Homepage →
              </Link>
              <a
                href="mailto:info@hvacbizbroker.com?subject=Free%20Business%20Valuation&body=Hello%20HVAC%20Biz%20Broker%20Team,%0A%0AI%20would%20like%20to%20get%20a%20free%20business%20valuation.%0A%0APlease%20contact%20me%20to%20get%20started.%0A%0AThank%20you."
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                Get Free Business Valuation →
              </a>
              <a
                href="mailto:info@hvacbizbroker.com?subject=Contact%20Advisors&body=Hello%20HVAC%20Biz%20Broker%20Team,%0A%0AI%20would%20like%20to%20contact%20your%20advisors%20regarding%20my%20HVAC%20business.%0A%0APlease%20let%20me%20know%20the%20best%20way%20to%20reach%20you.%0A%0AThank%20you."
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                Contact Our Advisors →
              </a>
            </div>
          </section>
        </motion.div>
      </article>

      <Footer />
    </div>
  );
};

export default ValuationPage;